import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/react";

import { OrderPriceSummary } from "common/types/priceSummary";
import { PaymentMethodOption } from "common/types";
import { deepCombineErrors } from "common/utils/errors";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import { useClientQuery } from "client/accounts/hooks";
import { getWalletPaymentItems, walletDisplayText } from "client/payments/utils";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";

import { useUpdateOrderPaymentMethodMutation } from "../hooks";
import { IOrder } from "../types";

type PaymentMethodFormProps = {
  onClose: () => void;
  order: IOrder;
};

const PaymentMethodForm = (props: PaymentMethodFormProps) => {
  const { order, onClose } = props;
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();
  const { data: client, isLoading: clientLoading, error: clientError } = useClientQuery();

  const methods = client ? client.payment_methods : [];
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<
    PaymentMethodOption | undefined
  >(order.payment_method);

  const stripe = useStripe();
  const {
    mutate: updateOrderPayment,
    isLoading: isUpdateLoading,
    error: updateError,
    reset: resetUpdateOrderPayment,
  } = useUpdateOrderPaymentMethodMutation(order.id, {
    onSuccess: onClose,
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          updateOrderPayment(data);
        }
      });
    },
  });

  const {
    mutateAsync: createWalletPaymentMethod,
    isLoading: isCreateWalletPaymentMethodLoading,
    error: createWalletPaymentMethodError,
  } = useCreatePaymentMethodMutation();

  const {
    supportedWallets,
    isLoading: isLoadingPaymentRequest,
    request: walletPaymentRequest,
  } = useWalletPaymentMethodRequest({
    label: `Order ${order.display_number}`,
    amount: order.total,
    paymentItems: getWalletPaymentItems<OrderPriceSummary>(order),
    onCreate: async (id: string) => {
      resetUpdateOrderPayment(); // reset the update order payment error
      const method = await createWalletPaymentMethod({
        payment_method: id,
      });
      updateOrderPayment({ payment_method_id: method.id });
    },
  });

  const canSubmit = !!(
    selectedPaymentOption &&
    !isCreateWalletPaymentMethodLoading &&
    !isUpdateLoading
  );

  const handleSubmit = () => {
    if (!selectedPaymentOption) {
      return;
    }

    if (selectedPaymentOption.id === "__wallet__") {
      walletPaymentRequest?.show();
      return;
    }

    if (typeof selectedPaymentOption.id === "number") {
      updateOrderPayment({ payment_method_id: selectedPaymentOption.id });
      return;
    }

    Sentry.captureMessage(
      `Unexpected placeholder payment method [${JSON.stringify(selectedPaymentOption)}] in OrderPaymentUpdateModal`,
      "error",
    );
  };
  if (clientLoading) {
    return <ModalLayout.Loading />;
  }

  if (clientError) {
    return <ModalLayout.FetchError entityName="Payment method" error={clientError} />;
  }

  const declineError = updateError?.data?.card_decline_info;

  const formError = createWalletPaymentMethodError || updateError;

  return (
    <>
      <ModalLayout.Form
        submitText={
          selectedPaymentOption?.id === "__wallet__"
            ? `Authorize ${walletDisplayText(selectedPaymentOption.wallet)} payment`
            : "Authorize Payment"
        }
        canSubmit={canSubmit}
        isLoading={isCreateWalletPaymentMethodLoading || isUpdateLoading}
        // Don't need to show generic error if there is a decline error
        error={declineError ? null : formError}
        fieldErrors={[deepCombineErrors(formError?.errors)]}
        onSubmit={handleSubmit}
      >
        {declineError && <CardPaymentDeclineAlert declineError={declineError} />}
        <PaymentMethodSelectPanel
          initialValue={order.payment_method}
          isLoading={isLoadingPaymentRequest}
          value={selectedPaymentOption}
          options={methods}
          walletPayment={supportedWallets}
          onAdd={() => openPaymentCreateDrawer({ onSuccess: setSelectedPaymentOption })}
          onChange={setSelectedPaymentOption}
        />
      </ModalLayout.Form>
    </>
  );
};

type PaymentUpdateModalProps = {
  visible: boolean;
  onClose: () => void;
  order: IOrder;
};

const OrderPaymentUpdateModal = (props: PaymentUpdateModalProps) => {
  const { visible, onClose, order } = props;
  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Update payment method" onClose={onClose} />
        <PaymentMethodForm order={order} onClose={onClose} />
      </ModalLayout>
    </Modal>
  );
};

export default OrderPaymentUpdateModal;
